import { Grid, Skeleton } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
} from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import {
	TermSheetFinancialReporting,
	CompanyFinancialStatementTypes,
	FacilityAmortizationPeriods,
	CompanyFinancialStatementTypesMap,
	FacilityAmortizationPeriodsMap,
	TermSheetReportingFormDto,
} from '../../../shared/models';
import { FormikUtils, NumberUtils } from '../../../shared/utils';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getApiResponseData } from '../../../shared/utils/axios';

interface ReportingFormProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	reporting: TermSheetFinancialReporting | null;
	close: () => void;
	isLoading: boolean;
}

export const ReportingForm: React.FC<ReportingFormProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	reporting,
	close,
	isLoading,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const reportingMutation = useMutation(
		async ({ values }: ReportingFormMutationVariables) => {
			const patchValues = {
				reportingQuality: values.reportingQuality,
				frequency: values.frequency,
				daysToDeliver: NumberUtils.ParseDecimal(values.daysToDeliver, undefined),
			} as TermSheetReportingFormDto;

			if (reporting != null && reporting.id == 0) {
				const { data, error } = await getApiResponseData<void>(
					api.post(
						CerebroApiEndpoints.TermSheets.Reporting(dataRoomGuid, termSheetGuid, revisionGuid),
						patchValues
					)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			} else if (reporting != null && reporting.id > 0) {
				const { data, error } = await getApiResponseData<void>(
					api.patch(
						CerebroApiEndpoints.TermSheets.ReportingSpecific(
							dataRoomGuid,
							termSheetGuid,
							revisionGuid,
							reporting?.guid
						),
						patchValues
					)
				);

				if (error) {
					throw new Error(error.response?.data);
				}

				return data;
			}

			return null;
		},
		{
			onSuccess: () => {
				snackbar.enqueueSnackbar('Term sheet reporting was updated.', { variant: 'success' });
				client.invalidateQueries(QueryKeys.TermSheets.Revision(dataRoomGuid, termSheetGuid, revisionGuid));
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	if (isLoading) {
		return (
			<>
				<Skeleton height={60} />
				<Skeleton height={60} />
				<Skeleton height={60} />
			</>
		);
	}

	return (
		<CerebroDrawer open={reporting != null} onClose={close}>
			<Formik
				initialValues={{
					reportingQuality: reporting?.reportingQuality,
					frequency: reporting?.frequency,
					daysToDeliver: NumberUtils.FormatDecimal(reporting?.daysToDeliver),
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(reportingMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values }) => {
					return (
						<CerebroForm flatIndicator onSubmit={handleSubmit} sx={{ marginTop: 3, paddingRight: 3 }}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								<Grid item xs={11}>
									<CerebroLabel id="reportingQuality" required>
										What is the reporting quality?
									</CerebroLabel>
									<Field
										id="reportingQuality"
										as={CerebroDropdown}
										name="reportingQuality"
										disabled={isSubmitting}
									>
										{Object.entries(CompanyFinancialStatementTypesMap).map(
											([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											)
										)}
									</Field>
									{errors.reportingQuality && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.reportingQuality}
										</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="frequency" required>
										What is the report frequency?
									</CerebroLabel>
									<Field id="frequency" as={CerebroDropdown} name="frequency" disabled={isSubmitting}>
										{Object.entries(FacilityAmortizationPeriodsMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
									{errors.frequency && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.frequency}
										</CerebroText>
									)}
								</Grid>

								<Grid item xs={11}>
									<CerebroLabel id="daysToDeliver" required>
										Number of days to deliver?
									</CerebroLabel>
									<Field
										id="daysToDeliver"
										as={CerebroMaskedInput}
										name="daysToDeliver"
										disabled={isSubmitting}
										error={touched.daysToDeliver && errors.daysToDeliver !== undefined}
									/>
									{errors.daysToDeliver && (
										<CerebroText variant="error" sx={{ marginBottom: 4 }}>
											{errors.daysToDeliver}
										</CerebroText>
									)}
								</Grid>

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type ReportingFormMutationVariables = FormikUtils.MutateOnSubmitVariables<ReportingFormValues>;
interface ReportingFormValues {
	reportingQuality: CompanyFinancialStatementTypes | undefined;
	frequency: FacilityAmortizationPeriods | undefined;
	daysToDeliver: string;
}
type ReportingFormValueErrors = FormikErrors<ReportingFormValues>;

function validateForm(values: ReportingFormValues) {
	const errors: ReportingFormValueErrors = {};

	if (!values.reportingQuality) {
		errors.reportingQuality = 'You must select a reporting quality.';
	}

	if (!values.frequency) {
		errors.frequency = 'You must select a frequency.';
	}

	if (!values.daysToDeliver) {
		errors.daysToDeliver = 'You must enter a valid number of days.';
	}

	return errors;
}
